import { OrganizationLogo } from '@features/organizations'
import { ProcessSend } from '@st/redux'
import type { Organization } from '@st/sdk'
import { useProcess, useSelect } from '~/store'
import { Button, FullPageCard, Spinner } from '@st/theme'
import { P, match } from '@st/pattern'
import {
  OrganizationTeamInviteMessage,
  OrganizationTeamInviteState,
  organizationTeamInviteModule
} from './organization-team-invite-module'

type ActiveTeamInviteState = Exclude<OrganizationTeamInviteState, { status: 'idle' | 'finishing' }>

export function OrganizationTeamInvitationPage() {
  const teamInvitation = useProcess('organizationTeamInvite')
  const state = useSelect('organizationTeamInvite', (s) => s)
  const organization = useSelect('organization', (s) => s.organization)

  if (state.status == 'idle' || state.status == 'finishing' || !organization) {
    return (
      <FullPageCard>
        <div className="flex h-40 items-center justify-center">
          <Spinner className="size-8 fill-blue-500 text-blue-100" />
        </div>
      </FullPageCard>
    )
  }

  return (
    <OrganizationTeamInvitationInner
      state={state}
      organization={organization}
      send={teamInvitation.send}
    />
  )
}

function OrganizationTeamInvitationInner({
  state,
  organization,
  send
}: {
  state: ActiveTeamInviteState
  organization: Organization
  send: ProcessSend<OrganizationTeamInviteMessage>
}) {
  function onClickSend() {
    if (state.status != 'ready' && state.status != 'sendFailed') return
    send({ type: 'sendPasswordResetEmail' })
  }

  return (
    <FullPageCard>
      <div className="flex flex-col items-center gap-3">
        {organization.logo ? <OrganizationLogo src={organization.logo} size={80} /> : null}
        <h1 className="text-3xl">{organization.name}</h1>
      </div>

      <p className="mt-4 text-center text-sm">
        {match(state)
          .with(
            { status: P.union('ready', 'sending') },
            () =>
              `To finalize your account setup for ${organization.name}, please click the button below to recieve a secure link in your email reset your password.`
          )
          .with(
            { status: 'sendFailed' },
            () =>
              'We could not send the set password email. Please check your connection and try again.'
          )
          .with(
            { status: 'sent' },
            () =>
              `You have been emailed a secure link to reset your password. Please use it to set your
              password and login to ${organization.name}`
          )
          .exhaustive()}
      </p>

      {match(state.status)
        .with('ready', () => (
          <Button variant="primary" className="mx-auto mt-4" onClick={onClickSend}>
            Send set password email
          </Button>
        ))
        .with('sending', () => (
          <Button variant="primary" className="mx-auto mt-4" disabled={true}>
            Sending email
          </Button>
        ))
        .with('sendFailed', () => (
          <Button variant="primary" className="mx-auto mt-4" onClick={onClickSend}>
            Retry sending email
          </Button>
        ))
        .with('sent', () => <></>)
        .exhaustive()}
    </FullPageCard>
  )
}
